import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Scale, Plus, Trash2, Ruler, TrendingDown, Target, Info, Edit3, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Field metadata
const REQUIRED_MEAS = [
  { key: 'abdomen', label: 'Abdômen', tip: 'Fita na altura do umbigo, horizontal.', img: '/images/measurements/abdomen.png' },
  { key: 'cintura', label: 'Cintura', tip: 'Ponto mais estreito entre costelas e quadril.', img: '/images/measurements/cintura.png' },
  { key: 'quadril', label: 'Quadril', tip: 'Maior área dos glúteos.', img: '/images/measurements/quadril.png' },
];
const OPTIONAL_MEAS = [
  { key: 'braco_d', label: 'Braço D', tip: 'Meio do braço direito, braço relaxado.', img: '/images/measurements/bracos.png' },
  { key: 'braco_e', label: 'Braço E', tip: 'Meio do braço esquerdo, braço relaxado.', img: '/images/measurements/bracos.png' },
  { key: 'coxa_d', label: 'Coxa D', tip: '10 cm abaixo da prega glútea ou meio da coxa direita.', img: '/images/measurements/coxas.png' },
  { key: 'coxa_e', label: 'Coxa E', tip: '10 cm abaixo da prega glútea ou meio da coxa esquerda.', img: '/images/measurements/coxas.png' },
  { key: 'panturrilha_d', label: 'Panturrilha D', tip: 'Maior volume da panturrilha direita.', img: '/images/measurements/panturrilhas.png' },
  { key: 'panturrilha_e', label: 'Panturrilha E', tip: 'Maior volume da panturrilha esquerda.', img: '/images/measurements/panturrilhas.png' },
];
const ALL_MEAS = [...REQUIRED_MEAS, ...OPTIONAL_MEAS];
const MEAS_COLORS = { abdomen: '#e11d48', cintura: '#3b82f6', quadril: '#8b5cf6', braco_d: '#f59e0b', braco_e: '#f59e0b', coxa_d: '#f97316', coxa_e: '#f97316', panturrilha_d: '#10b981', panturrilha_e: '#10b981' };

const EMPTY_FORM = { weight: '', date: new Date().toISOString().split('T')[0] };
ALL_MEAS.forEach(m => { EMPTY_FORM[m.key] = ''; });

const fmtDate = (d) => new Date(d).toLocaleDateString('pt-BR');

// ============================================================
// GUIDE MODAL — "Como medir as circunferências"
// ============================================================
const HowToMeasureDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-2xl text-[#0F1B3D]">Como medir as circunferências</DialogTitle>
      </DialogHeader>
      <div className="space-y-6 pt-2">
        <div className="rounded-xl bg-[#FFF7F0] border border-[#A8174E]/20 p-4">
          <h4 className="font-semibold text-[#A8174E] mb-2 text-sm uppercase tracking-wide">Regras gerais</h4>
          <ul className="text-sm text-[#5F6B85] space-y-1.5 list-disc pl-5">
            <li>Meça sempre no mesmo ponto e com a mesma fita, no mesmo horário.</li>
            <li>Postura ereta, abdômen relaxado, fita justa sem apertar.</li>
            <li>Use fita métrica flexível, paralela ao chão.</li>
            <li>Compare sempre com suas medidas anteriores. Pequenas variações são normais.</li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-[#0F1B3D] text-sm uppercase tracking-wide mb-3">Passo a passo</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { n: 1, title: 'Abdômen', text: 'Fita na altura do umbigo, horizontal.', color: '#e11d48', img: '/images/measurements/abdomen.png' },
              { n: 2, title: 'Cintura', text: 'Ponto mais estreito entre costelas e quadril.', color: '#3b82f6', img: '/images/measurements/cintura.png' },
              { n: 3, title: 'Quadril', text: 'Maior área dos glúteos.', color: '#8b5cf6', img: '/images/measurements/quadril.png' },
              { n: 4, title: 'Braços', text: 'Meio do braço, braço relaxado.', color: '#f59e0b', img: '/images/measurements/bracos.png' },
              { n: 5, title: 'Coxas', text: '10 cm abaixo da prega glútea ou meio da coxa.', color: '#f97316', img: '/images/measurements/coxas.png' },
              { n: 6, title: 'Panturrilhas', text: 'Maior volume.', color: '#10b981', img: '/images/measurements/panturrilhas.png' },
            ].map(s => (
              <div key={s.n} className="rounded-xl border border-[#E5DCC8] overflow-hidden bg-white shadow-sm" data-testid={`meas-step-${s.n}`}>
                <div className="flex items-start gap-2 p-3">
                  <div className="w-7 h-7 rounded-full text-white flex items-center justify-center text-sm font-bold shrink-0" style={{ background: s.color }}>{s.n}</div>
                  <div>
                    <div className="font-semibold text-[#0F1B3D] leading-tight" style={{ color: s.color }}>{s.title}</div>
                    <div className="text-xs text-[#5F6B85] mt-0.5">{s.text}</div>
                  </div>
                </div>
                <img src={s.img} alt={s.title} className="w-full h-48 object-contain bg-[#FFF7F0]" loading="lazy" />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-[#FAF6EE] border border-[#E5DCC8] p-4">
          <h4 className="font-semibold text-[#0F1B3D] mb-1 text-sm uppercase tracking-wide">Circunferências principais</h4>
          <div className="text-sm text-[#5F6B85]">Abdômen · Cintura · Quadril</div>
          <p className="text-xs text-[#7A85A0] mt-1">Todos os campos de medidas são opcionais. Preencha apenas o que medir no dia.</p>
          <h4 className="font-semibold text-[#0F1B3D] mb-1 text-sm uppercase tracking-wide mt-3">Demais medidas</h4>
          <div className="text-sm text-[#5F6B85]">Braço D/E · Coxa D/E · Panturrilha D/E</div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ============================================================
// MAIN COMPONENT
// ============================================================
export const PatientEvolution = () => {
  const [weights, setWeights] = useState([]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [prontuario, setProntuario] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [howToOpen, setHowToOpen] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingEntry, setEditingEntry] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [patientWeights, pi] = await Promise.all([
        axios.get(`${API_URL}/api/patient/weights`, { withCredentials: true }),
        axios.get(`${API_URL}/api/patient/my-info`, { withCredentials: true })
      ]);
      setWeights(patientWeights.data);
      setPatientInfo(pi.data);
      if (pi.data?.patient_id) {
        const { data: pron } = await axios.get(`${API_URL}/api/prontuario-summary/${pi.data.patient_id}`, { withCredentials: true }).catch(() => ({ data: {} }));
        setProntuario(pron?.exists ? pron : null);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isTirz = patientInfo?.consultation_type === 'tirzepatida';

  const resetForm = () => { setForm(EMPTY_FORM); setEditingEntry(null); setShowOptional(false); };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.weight) { toast.error('Informe o peso'); return; }
    const payload = { weight: parseFloat(form.weight), date: form.date || null };
    ALL_MEAS.forEach(m => {
      if (form[m.key] !== '' && form[m.key] !== null && form[m.key] !== undefined) {
        payload[m.key] = parseFloat(form[m.key]);
      }
    });
    try {
      if (editingEntry) {
        await axios.put(`${API_URL}/api/patient/weight/${editingEntry}`, payload, { withCredentials: true });
        toast.success('Registro atualizado!');
      } else {
        await axios.post(`${API_URL}/api/patient/weight`, payload, { withCredentials: true });
        toast.success('Registro salvo!');
      }
      setIsAddOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao salvar'); }
  };

  const handleEdit = (w) => {
    const f = { ...EMPTY_FORM, weight: String(w.weight || ''), date: w.created_at?.split('T')[0] || '' };
    ALL_MEAS.forEach(m => { f[m.key] = w[m.key] != null ? String(w[m.key]) : ''; });
    setForm(f);
    setEditingEntry(w.record_id);
    setShowOptional(OPTIONAL_MEAS.some(m => w[m.key] != null));
    setIsAddOpen(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Remover este registro?')) return;
    try {
      await axios.delete(`${API_URL}/api/patient/weight/${entryId}`, { withCredentials: true });
      toast.success('Removido!');
      fetchData();
    } catch (error) { toast.error('Erro ao remover'); }
  };

  const sorted = useMemo(() => [...weights].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)), [weights]);
  const latest = sorted[sorted.length - 1] || null;
  const first = sorted[0] || null;
  const weightDiff = latest && first ? (latest.weight - first.weight).toFixed(1) : 0;
  const pesoInicial = prontuario?.peso_inicial;
  const pesoCombinado = prontuario?.peso_combinado;

  const chartData = sorted.map(w => ({ date: fmtDate(w.created_at), peso: w.weight }));
  const measChartData = sorted.map(w => {
    const row = { date: fmtDate(w.created_at) };
    ALL_MEAS.forEach(m => { if (w[m.key] != null) row[m.key] = w[m.key]; });
    return row;
  });

  // Circumference summary — variation from first record to latest
  const measSummary = useMemo(() => {
    return ALL_MEAS.map(m => {
      const firstVal = sorted.find(w => w[m.key] != null)?.[m.key];
      const lastVal = [...sorted].reverse().find(w => w[m.key] != null)?.[m.key];
      if (lastVal == null) return null;
      const delta = firstVal != null ? +(lastVal - firstVal).toFixed(1) : null;
      return { ...m, current: lastVal, delta };
    }).filter(Boolean);
  }, [sorted]);

  // Non-tirzepatida sees message
  if (!isTirz) {
    return (
      <div className="space-y-6" data-testid="patient-evolution">
        <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Registro de Peso e Medidas</h2>
        <Card className="border-[#E5DCC8]">
          <CardContent className="p-8 text-center">
            <Scale className="h-12 w-12 text-[#7A85A0] mx-auto mb-3" />
            <p className="text-[#5F6B85] font-medium">Funcionalidade disponível para tratamento Tirzepatida</p>
            <p className="text-sm text-[#7A85A0] mt-1">Consulte sua médica para mais informações</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patient-evolution">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Registro de Peso e Medidas</h2>
          <p className="text-[#5F6B85]">Acompanhe seu progresso corporal</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-[#E5DCC8]" onClick={() => setHowToOpen(true)} data-testid="how-to-measure-btn">
            <Info className="h-4 w-4 mr-2" /> Como medir
          </Button>
          <Dialog open={isAddOpen} onOpenChange={(o) => { setIsAddOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="add-weight-button">
                <Plus className="h-4 w-4 mr-2" /> Novo registro
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#0F1B3D]">
                  <Ruler className="h-5 w-5 text-[#A8174E]" />
                  {editingEntry ? 'Editar registro' : 'Novo registro de peso e medidas'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleAdd} className="space-y-5">
                {/* Basic — date + weight */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Data *</Label>
                    <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Peso (kg) *</Label>
                    <Input type="number" step="0.1" min="1" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} required placeholder="Ex: 78.5" data-testid="weight-input" />
                  </div>
                </div>

                {/* How to measure hint */}
                <div className="flex items-center justify-between rounded-lg bg-[#FFF7F0] border border-[#A8174E]/20 px-3 py-2">
                  <span className="text-xs text-[#5F6B85]">Dica: fita justa sem apertar, postura ereta, sempre no mesmo horário.</span>
                  <button type="button" onClick={() => setHowToOpen(true)} className="text-xs font-medium text-[#A8174E] hover:underline">
                    Ver guia completo
                  </button>
                </div>

                {/* Main measurements */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-[#0F1B3D]">Circunferências principais</h4>
                    <span className="text-xs text-[#7A85A0]">em cm · opcionais</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {REQUIRED_MEAS.map(m => (
                      <div key={m.key} className="rounded-lg border border-[#E5DCC8] overflow-hidden bg-white">
                        <img src={m.img} alt={m.label} className="w-full h-28 object-cover bg-[#FFF7F0]" loading="lazy" />
                        <div className="p-2.5 space-y-1.5">
                          <Label className="text-sm flex items-center gap-1.5">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: MEAS_COLORS[m.key] }} />
                            {m.label}
                          </Label>
                          <Input
                            type="number" step="0.1" min="0"
                            value={form[m.key]}
                            onChange={e => setForm({ ...form, [m.key]: e.target.value })}
                            placeholder="cm"
                            data-testid={`meas-${m.key}`}
                          />
                          <p className="text-xs text-[#7A85A0]">{m.tip}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Optional measurements — collapsible */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowOptional(v => !v)}
                    className="w-full flex items-center justify-between text-left rounded-lg border border-[#E5DCC8] px-3 py-2 hover:bg-[#FAF6EE] transition-colors"
                    data-testid="toggle-optional-meas"
                  >
                    <span className="font-medium text-[#0F1B3D] text-sm">Circunferências opcionais (Braço, Coxa, Panturrilha)</span>
                    {showOptional ? <ChevronUp className="h-4 w-4 text-[#5F6B85]" /> : <ChevronDown className="h-4 w-4 text-[#5F6B85]" />}
                  </button>
                  {showOptional && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                      {OPTIONAL_MEAS.map(m => (
                        <div key={m.key} className="rounded-lg border border-[#E5DCC8] overflow-hidden bg-white">
                          <img src={m.img} alt={m.label} className="w-full h-24 object-cover bg-[#FFF7F0]" loading="lazy" />
                          <div className="p-2 space-y-1.5">
                            <Label className="text-sm flex items-center gap-1.5">
                              <span className="inline-block w-2 h-2 rounded-full" style={{ background: MEAS_COLORS[m.key] }} />
                              {m.label}
                            </Label>
                            <Input
                              type="number" step="0.1" min="0"
                              value={form[m.key]}
                              onChange={e => setForm({ ...form, [m.key]: e.target.value })}
                              placeholder="cm"
                              data-testid={`meas-${m.key}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white h-11" data-testid="submit-weight-form">
                  {editingEntry ? 'Atualizar registro' : 'Salvar registro'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-[#E5DCC8]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#0F1B3D]/10 rounded-lg"><Scale className="h-5 w-5 text-[#0F1B3D]" /></div>
              <div>
                <p className="text-xs text-[#7A85A0]">Peso Atual</p>
                <p className="text-xl font-bold text-[#0F1B3D]">{latest ? `${latest.weight} kg` : '-'}</p>
                {latest && <p className="text-[10px] text-[#7A85A0]">{fmtDate(latest.created_at)}</p>}
              </div>
            </div>
          </CardContent>
        </Card>
        {pesoCombinado && (
          <Card className="border-[#E5DCC8] border-l-4 border-l-[#B8845C]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#B8845C]/10 rounded-lg"><Target className="h-5 w-5 text-[#B8845C]" /></div>
                <div>
                  <p className="text-xs text-[#7A85A0]">Meta Combinada</p>
                  <p className="text-xl font-bold text-[#B8845C]">{pesoCombinado} kg</p>
                  {latest && <p className="text-[10px] text-[#7A85A0]">Faltam {Math.max(0, latest.weight - pesoCombinado).toFixed(1)} kg</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="border-[#E5DCC8]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#0F1B3D]/10 rounded-lg"><TrendingDown className="h-5 w-5 text-[#0F1B3D]" /></div>
              <div>
                <p className="text-xs text-[#7A85A0]">Variação Peso</p>
                <p className={`text-xl font-bold ${parseFloat(weightDiff) < 0 ? 'text-[#0F1B3D]' : 'text-[#A8174E]'}`}>{weightDiff} kg</p>
                <p className="text-[10px] text-[#7A85A0]">Desde o início</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5DCC8]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#A8174E]/10 rounded-lg"><Ruler className="h-5 w-5 text-[#A8174E]" /></div>
              <div>
                <p className="text-xs text-[#7A85A0]">Total de Registros</p>
                <p className="text-xl font-bold text-[#A8174E]">{weights.length}</p>
                <p className="text-[10px] text-[#7A85A0]">Registros realizados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* WEIGHT EVOLUTION CHART */}
      {chartData.length >= 2 && (
        <Card className="border-[#E5DCC8]">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Scale className="h-4 w-4 text-[#0F1B3D]" /> Evolução do Peso</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DCC8" />
                <XAxis dataKey="date" tick={{ fill: '#5F6B85', fontSize: 11 }} />
                <YAxis tick={{ fill: '#5F6B85', fontSize: 11 }} domain={['dataMin - 3', 'dataMax + 3']} />
                <Tooltip />
                {pesoInicial && <ReferenceLine y={pesoInicial} stroke="#7A85A0" strokeDasharray="5 5" label={{ value: `Início: ${pesoInicial}kg`, fill: '#7A85A0', fontSize: 10 }} />}
                {pesoCombinado && <ReferenceLine y={pesoCombinado} stroke="#B8845C" strokeDasharray="5 5" label={{ value: `Meta: ${pesoCombinado}kg`, fill: '#B8845C', fontSize: 10 }} />}
                <Line type="monotone" dataKey="peso" stroke="#0F1B3D" strokeWidth={2.5} name="Peso (kg)" dot={{ fill: '#0F1B3D', r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* CIRCUMFERENCE SUMMARY */}
      {measSummary.length > 0 && (
        <Card className="border-[#E5DCC8]">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Ruler className="h-4 w-4 text-[#A8174E]" /> Resumo das Circunferências</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
              {measSummary.map(m => (
                <div key={m.key} className="flex items-center justify-between border-b border-[#FAF6EE] py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: MEAS_COLORS[m.key] }} />
                    <span className="text-sm text-[#5F6B85]">{m.label}</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="font-semibold text-[#0F1B3D]">{m.current} cm</span>
                    {m.delta != null && m.delta !== 0 && (
                      <span className={`text-xs font-medium ${m.delta < 0 ? 'text-[#10b981]' : 'text-[#A8174E]'}`}>
                        {m.delta < 0 ? '' : '+'}{m.delta} cm
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CIRCUMFERENCE EVOLUTION CHART */}
      {measChartData.length >= 2 && measSummary.length > 0 && (
        <Card className="border-[#E5DCC8]">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Ruler className="h-4 w-4 text-[#0F1B3D]" /> Evolução das Circunferências</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={measChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DCC8" />
                <XAxis dataKey="date" tick={{ fill: '#5F6B85', fontSize: 11 }} />
                <YAxis tick={{ fill: '#5F6B85', fontSize: 11 }} unit=" cm" />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {measSummary.map(m => (
                  <Line key={m.key} type="monotone" dataKey={m.key} stroke={MEAS_COLORS[m.key]} strokeWidth={2} name={m.label} dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* HISTORY TABLE */}
      <Card className="border-[#E5DCC8]">
        <CardHeader><CardTitle className="text-base">Histórico de Registros</CardTitle></CardHeader>
        <CardContent>
          {weights.length === 0 ? (
            <div className="text-center py-8">
              <Scale className="h-10 w-10 text-[#7A85A0] mx-auto mb-3" />
              <p className="text-[#7A85A0] font-medium">Nenhum registro ainda</p>
              <p className="text-sm text-[#7A85A0] mt-1">Clique em &quot;Novo registro&quot; para começar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#7A85A0] border-b border-[#E5DCC8]">
                    <th className="pb-2 pr-3">Data</th>
                    <th className="pb-2 pr-3">Peso</th>
                    {ALL_MEAS.map(m => <th key={m.key} className="pb-2 pr-3 whitespace-nowrap">{m.label}</th>)}
                    <th className="pb-2 pr-3 w-16">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {[...weights].reverse().map(w => (
                    <tr key={w.record_id} className="border-b border-[#FAF6EE]" data-testid={`weight-entry-${w.record_id}`}>
                      <td className="py-2.5 pr-3 font-medium text-[#0F1B3D] whitespace-nowrap">{fmtDate(w.created_at)}</td>
                      <td className="py-2.5 pr-3 font-semibold text-[#0F1B3D] whitespace-nowrap">{w.weight} kg</td>
                      {ALL_MEAS.map(m => (
                        <td key={m.key} className="py-2.5 pr-3 text-[#5F6B85] whitespace-nowrap">{w[m.key] != null ? `${w[m.key]} cm` : '—'}</td>
                      ))}
                      <td className="py-2.5 pr-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(w)} className="h-7 w-7 p-0 text-[#5F6B85] hover:text-[#0F1B3D]" title="Editar"><Edit3 className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(w.record_id)} className="h-7 w-7 p-0 text-[#A8174E]" title="Remover"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <HowToMeasureDialog open={howToOpen} onOpenChange={setHowToOpen} />
    </div>
  );
};
